import { useState } from "react";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import { getAvatarColorClass } from "@/components/ui/avatar";

// A small avatar for inline-editable rows. It ALWAYS paints a visible base —
// colored initials, or a bordered person icon when there are none — and layers
// the image (gravatar / company favicon) on top. So:
//   - a real photo/favicon covers the base (you see the image);
//   - a blank/transparent favicon lets the initials show through instead of a
//     blank circle (favicon.show returns a 200 placeholder — not a 404 — for
//     domains with no favicon, so the <img> "loads" and never errors);
//   - a URL that hard-errors falls back to the base too.
// Using a plain <img> (not Radix Avatar) also avoids the fallback flicker on
// src change when reassigning/switching a contact.
export function InlineAvatar({
  src,
  fallback,
  className,
  title,
}: {
  src?: string | null;
  fallback: string;
  className?: string;
  title?: string;
}) {
  // Track the exact src that failed so a new src renders the <img> again
  // immediately (no fallback frame) instead of staying on the base.
  const [erroredSrc, setErroredSrc] = useState<string | null>(null);
  const label = fallback.trim();
  const showImg = !!src && erroredSrc !== src;

  return (
    <span
      title={title}
      className={cn(
        "relative w-6 h-6 rounded-full shrink-0 flex items-center justify-center overflow-hidden",
        label
          ? cn("text-[10px] font-medium", getAvatarColorClass(label))
          : "border border-muted-foreground/40 text-muted-foreground",
        className,
      )}
    >
      {label || <User className="size-3.5" />}
      {showImg && (
        <img
          src={src as string}
          alt=""
          onError={() => setErroredSrc(src as string)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </span>
  );
}
